import { message } from 'antd'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components/macro'
import {
  HARBOUR_NOTES_ADD,
  HARBOUR_NOTES_EMPTY,
  HARBOUR_NOTES_HINT,
  HARBOUR_NOTES_PIN,
  HARBOUR_NOTES_PINNED,
  HARBOUR_NOTES_PIN_THIS,
  HARBOUR_NOTES_PLACEHOLDER,
  HARBOUR_NOTES_UNPIN,
} from 'src/constants/harbour'
import { SPARTACUS_NOTE_TEXT_MAX_LENGTH } from 'src/constants/spartacusNotes'
import {
  buildHarbourSpartacusNoteCreateBody,
  formatHarbourNotesOverflowCaption,
  formatHarbourNotesTitle,
  formatHarbourNoteWhen,
  getHarbourNotesTotal,
  mapHarbourSpartacusNotes,
  mergeHarbourSpartacusNote,
  replaceHarbourSpartacusNote,
} from 'src/utils/harbourNotes'
import {
  createSpartacusVenueNote,
  setSpartacusVenueNotePinned,
} from 'src/utils/spartacusApi'
import {
  formatSpartacusNoteAuthorLabel,
  mapSpartacusVenueNote,
} from 'src/utils/spartacusMappers'
import {
  toastHarbourNoteAdded,
  toastHarbourNotePinChanged,
} from 'src/utils/harbourToast'

/**
 * Spartacus venue notebook in the Harbour briefing.
 * GET hydrates the thread; create / pin use Spartacus note APIs.
 */
const HarbourDrawerNotes = ({
  drawer = {},
  visible = false,
  venueId = null,
} = {}) => {
  const incoming = mapHarbourSpartacusNotes(drawer.notes)
  const incomingKey = incoming.notes
    .map(
      note =>
        `${note.noteId}:${note.pinned}:${
          note.updatedAt || note.createdAt || ''
        }`,
    )
    .join('|')
  const [notes, setNotes] = useState(incoming.notes)
  const [noteText, setNoteText] = useState('')
  const [pinOnCreate, setPinOnCreate] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [pinningNoteId, setPinningNoteId] = useState(null)

  useEffect(() => {
    setNotes(mapHarbourSpartacusNotes(drawer.notes).notes)
  }, [incomingKey])

  if (!visible) {
    return null
  }

  const notesTotal = getHarbourNotesTotal(notes, incoming.pagination)
  const overflow = formatHarbourNotesOverflowCaption(notes.length, notesTotal)

  const handleAdd = () => {
    const body = buildHarbourSpartacusNoteCreateBody({
      noteText,
      pinned: pinOnCreate,
    })
    if (!body.noteText) {
      message.warning('Please enter a note before saving')
      return
    }
    if (!venueId || submitting) {
      return
    }

    async function addNote() {
      setSubmitting(true)
      try {
        const created = await createSpartacusVenueNote(venueId, body)
        setNotes(prev => mergeHarbourSpartacusNote(prev, created))
        setNoteText('')
        setPinOnCreate(false)
        toastHarbourNoteAdded({ pinned: Boolean(body.pinned) })
      } catch (err) {
        message.error(err.message || 'Failed to add note')
      } finally {
        setSubmitting(false)
      }
    }

    addNote()
  }

  const handleTogglePin = note => {
    if (!venueId || !note?.noteId || pinningNoteId) {
      return
    }

    async function togglePin() {
      setPinningNoteId(note.noteId)
      try {
        const updated = await setSpartacusVenueNotePinned(
          venueId,
          note.noteId,
          !note.pinned,
        )
        const mapped = mapSpartacusVenueNote(
          updated?.note && typeof updated.note === 'object'
            ? updated.note
            : updated,
        )
        setNotes(prev => replaceHarbourSpartacusNote(prev, mapped))
        toastHarbourNotePinChanged(Boolean(mapped?.pinned))
      } catch (err) {
        message.error(err.message || 'Failed to update pin')
      } finally {
        setPinningNoteId(null)
      }
    }

    togglePin()
  }

  return (
    <Block data-testid='harbour-notes'>
      <BlockLabel data-testid='harbour-notes-title'>
        {formatHarbourNotesTitle(notesTotal)}
      </BlockLabel>
      <Caption>{HARBOUR_NOTES_HINT}</Caption>

      <Compose>
        <NoteInput
          data-testid='harbour-note-compose'
          rows={3}
          maxLength={SPARTACUS_NOTE_TEXT_MAX_LENGTH}
          value={noteText}
          placeholder={HARBOUR_NOTES_PLACEHOLDER}
          onChange={event => setNoteText(event.target.value)}
        />
        <ComposeRow>
          <PinLabel>
            <input
              type='checkbox'
              checked={pinOnCreate}
              onChange={event => setPinOnCreate(event.target.checked)}
              data-testid='harbour-note-compose-pin'
            />
            {HARBOUR_NOTES_PIN_THIS}
          </PinLabel>
          <AddNote
            type='button'
            disabled={submitting || !venueId}
            data-testid='harbour-note-add'
            onClick={handleAdd}
          >
            {submitting ? 'Adding…' : HARBOUR_NOTES_ADD}
          </AddNote>
        </ComposeRow>
      </Compose>

      {overflow ? (
        <Overflow data-testid='harbour-notes-overflow'>{overflow}</Overflow>
      ) : null}
      {notes.length === 0 ? (
        <Empty data-testid='harbour-notes-empty'>{HARBOUR_NOTES_EMPTY}</Empty>
      ) : (
        <Timeline>
          {notes.map((note, index) => {
            const noteId = note.noteId || note.key || `note-${index}`
            const pinning = pinningNoteId === note.noteId
            return (
              <Note
                key={noteId}
                data-testid={`harbour-note-${noteId}`}
                data-pinned={note.pinned ? 'true' : 'false'}
              >
                <NoteHeader>
                  <Meta>
                    <Author data-testid={`harbour-note-author-${noteId}`}>
                      {formatSpartacusNoteAuthorLabel(note.author)}
                    </Author>
                    <When data-testid={`harbour-note-when-${noteId}`}>
                      {formatHarbourNoteWhen(note.createdAt)}
                    </When>
                  </Meta>
                  <HeaderActions>
                    {note.pinned ? (
                      <Pinned data-testid={`harbour-note-pinned-${noteId}`}>
                        {HARBOUR_NOTES_PINNED}
                      </Pinned>
                    ) : null}
                    <PinButton
                      type='button'
                      disabled={pinning || !venueId}
                      data-testid={`harbour-note-pin-${noteId}`}
                      onClick={() => handleTogglePin(note)}
                    >
                      {pinning
                        ? '…'
                        : note.pinned
                        ? HARBOUR_NOTES_UNPIN
                        : HARBOUR_NOTES_PIN}
                    </PinButton>
                  </HeaderActions>
                </NoteHeader>
                <NoteText data-testid={`harbour-note-text-${noteId}`}>
                  {note.noteText}
                </NoteText>
              </Note>
            )
          })}
        </Timeline>
      )}
    </Block>
  )
}

const Block = styled.section`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const BlockLabel = styled.h5`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

const Caption = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const Overflow = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const Empty = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const Compose = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const NoteInput = styled.textarea`
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 8px;
  font: inherit;
  font-size: 13px;
  resize: vertical;
`

const ComposeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const PinLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  cursor: pointer;
`

const AddNote = styled.button`
  border: 0;
  background: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
  color: #fff;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Note = styled.article`
  padding: 8px 10px;
  border: 1px solid #e6e8eb;
  border-radius: 6px;
  background: ${({ theme }) => theme.colours.white || '#fff'};

  &[data-pinned='true'] {
    border-color: #c9decb;
    background: #f4f8f6;
  }
`

const NoteHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

const Meta = styled.div`
  min-width: 0;
`

const Author = styled.div`
  font-size: 12px;
  font-weight: 600;
`

const When = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
`

const Pinned = styled.span`
  flex: 0 0 auto;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #2d5a1e;
  background: #e4f0e6;
`

const PinButton = styled.button`
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  border-radius: 6px;
  padding: 2px 8px;
  cursor: pointer;
  font-size: 12px;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const NoteText = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
`

export default HarbourDrawerNotes
